/** Font evidence is a candidate for matching the image, never an instruction
 * to replace the type on the ad with the brand's preferred typeface. */
export interface CreativeFontCandidate { family: string; weight?: string }
export interface CreativeFontFace { family: string; src: string; weight?: string; style?: string; unicodeRange?: string }
export interface CreativeFontEvidence {
  fontCandidates?: CreativeFontCandidate[];
  fontFaces?: CreativeFontFace[];
}
const faceLoads = new Map<string, Promise<boolean>>();
const runtimeFamilies = new Map<string, string>();

/** CSS font registrations are global to the page. Scope each source set to
 * its own runtime family so two brands called "Display" cannot replace one
 * another's glyphs. The document always stores the original display name. */
export function creativeFontFamily(family: string, evidence: CreativeFontEvidence = {}): string {
  const faces = (evidence.fontFaces ?? []).filter(face => face.family === family);
  if (!faces.length) return family;
  const key = JSON.stringify([family, faces.map(face => JSON.stringify([
    face.src, face.weight || '400', face.style || 'normal', face.unicodeRange || '',
  ])).sort()]);
  let alias = runtimeFamilies.get(key);
  if (!alias) {
    alias = `AdmojiCreativeFont${runtimeFamilies.size + 1}`;
    runtimeFamilies.set(key, alias);
  }
  return alias;
}

export async function loadCreativeFontFaces(evidence: CreativeFontEvidence): Promise<Set<string>> {
  const loaded = new Set<string>();
  await Promise.all((evidence.fontFaces ?? []).map(async spec => {
    const alias = creativeFontFamily(spec.family, evidence);
    const key = JSON.stringify([alias, spec]);
    let pending = faceLoads.get(key);
    if (!pending) {
      pending = (async () => {
        try {
          const src = /^(?:https?:\/\/|data:font\/|data:application\/)/i.test(spec.src) ? `url(${JSON.stringify(spec.src)})` : spec.src;
          const face = new FontFace(alias, src, { weight: spec.weight || '400', style: spec.style || 'normal', ...(spec.unicodeRange ? { unicodeRange: spec.unicodeRange } : {}) });
          let timer: ReturnType<typeof setTimeout>;
          const timeout = new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), 8000); });
          const result = await Promise.race([face.load(), timeout]).finally(() => clearTimeout(timer));
          if (!result) return false;
          document.fonts.add(face);
          return true;
        } catch { return false; }
      })();
      faceLoads.set(key, pending);
    }
    if (await pending) loaded.add(spec.family);
    else faceLoads.delete(key);
  }));
  return loaded;
}

export function creativeFontWeight(style: string): number {
  return Number(style.match(/\b([1-9]\d{2})\b/)?.[1]) || (style.includes('bold') ? 700 : 400);
}
